import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ShoppingCart, Search, Eye, Truck, User, Building, X, Package, Shield, ExternalLink, Zap, AlertCircle, CheckCircle, Clock, XCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminAmazonOrders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    useEffect(() => {
        filterOrders();
    }, [searchTerm, orders]);

    const fetchOrders = async () => {
        try {
            const response = await axios.get(`${API}/admin/amazon/orders`);
            setOrders(response.data);
            setFilteredOrders(response.data);
        } catch (error) {
            toast.error("Erè nan chèche kòmand yo");
        } finally {
            setLoading(false);
        }
    };

    const filterOrders = () => {
        if (!searchTerm) {
            setFilteredOrders(orders);
            return;
        }
        const term = searchTerm.toLowerCase();
        const filtered = orders.filter(o =>
            (o.order_number || "").toLowerCase().includes(term) ||
            (o.buyer_name || "").toLowerCase().includes(term) ||
            (o.buyer_email || "").toLowerCase().includes(term)
        );
        setFilteredOrders(filtered);
    };

    const getStatusBadge = (status) => {
        const config = {
            completed: { class: "bg-green-600 text-white shadow-green-100", label: "Konplete", icon: CheckCircle },
            approved: { class: "bg-blue-600 text-white shadow-blue-100", label: "Apwouve", icon: Zap },
            rejected: { class: "bg-red-500 text-white shadow-red-100", label: "Rejte", icon: XCircle },
            pending_approval: { class: "bg-amber-500 text-white shadow-amber-100", label: "Atant", icon: Clock }
        };
        const st = config[status] || { class: "bg-gray-500 text-white", label: status, icon: Clock };
        return (
            <span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg ${st.class}`}>
                <st.icon className="w-3 h-3" /> {st.label}
            </span>
        );
    };

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-4xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Kòmand <span className="text-[#FF9900]">Amazon</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                            Tout pwodwi Amazon kliyan yo bay BVG achte pou yo.
                        </p>
                    </div>
                    
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                            placeholder="Chèche pa nimewo oswa kliyan..."
                            className="h-16 rounded-[25px] bg-white border-0 shadow-2xl pl-16 pr-8 font-black text-xs uppercase tracking-wider text-gray-900 focus:ring-0 focus:border-[#FF9900] transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-32">
                        <div className="w-14 h-14 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <Card className="border-0 shadow-[0_50px_100px_-20px_rgba(255,153,0,0.12)] rounded-[45px] overflow-hidden bg-white">
                        <CardContent className="p-0">
                            {filteredOrders.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100/50">
                                                <th className="py-8 px-10 text-left">Nimewo Kòmand</th>
                                                <th className="py-8 px-8 text-left">Tip / Kliyan</th>
                                                <th className="py-8 px-8 text-left">Amazon Subtotal</th>
                                                <th className="py-8 px-8 text-left">Haiti Shipping</th>
                                                <th className="py-8 px-8 text-left">Total HTG</th>
                                                <th className="py-8 px-8 text-left">Estati</th>
                                                <th className="py-8 px-10 text-center">Aksyon</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {filteredOrders.map(o => (
                                                <tr key={o.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0 group">
                                                    <td className="py-8 px-10 font-black font-mono text-gray-900 group-hover:text-[#FF9900] transition-colors">{o.order_number}</td>
                                                    <td className="py-8 px-8">
                                                        <div className="flex flex-col gap-1">
                                                            <div className="flex items-center gap-2">
                                                                {o.source === 'enterprise' ? (
                                                                    <span className="px-2 py-1 rounded-md bg-blue-50 text-[9px] font-black text-blue-600 uppercase flex items-center gap-1">
                                                                        <Building className="w-2.5 h-2.5" /> Entèpriz
                                                                    </span>
                                                                ) : (
                                                                    <span className="px-2 py-1 rounded-md bg-green-50 text-[9px] font-black text-green-600 uppercase flex items-center gap-1">
                                                                        <User className="w-2.5 h-2.5" /> Kliyan
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="font-black text-gray-800 text-xs mt-1">{o.buyer_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8 font-black text-gray-500 italic tracking-tighter">${o.amazon_subtotal?.toFixed(2)}</td>
                                                    <td className="py-8 px-8">
                                                        {o.haiti_shipping_fee > 0 ? (
                                                            <div className="flex flex-col">
                                                                <span className="font-black text-gray-900 tracking-tighter">${o.haiti_shipping_fee.toFixed(2)}</span>
                                                                <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">({o.pay_shipping_now ? 'Peye Devan' : 'Peye an Ayiti'})</span>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="py-8 px-8 font-black text-[#FF9900] text-lg italic tracking-tighter">{o.total_htg?.toLocaleString()} <span className="text-[10px] text-gray-400 not-italic ml-0.5">HTG</span></td>
                                                    <td className="py-8 px-8">
                                                        {getStatusBadge(o.status)}
                                                    </td>
                                                    <td className="py-8 px-10 text-center">
                                                        <Button
                                                            variant="ghost"
                                                            className="w-12 h-12 p-0 rounded-2xl bg-gray-50 text-gray-400 hover:bg-[#232F3E] hover:text-white transition-all shadow-sm hover:scale-110 active:scale-95"
                                                            onClick={() => setSelectedOrder(o)}
                                                        >
                                                            <Eye className="w-5 h-5" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-48 text-gray-400 flex flex-col items-center">
                                    <ShoppingCart className="w-24 h-24 mb-8 opacity-5 italic" />
                                    <p className="font-black uppercase tracking-widest text-[11px] leading-loose">Pa gen okenn kòmand Amazon ki koresponn.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Detail Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-300">
                    <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl rounded-[45px] bg-white">
                        <CardHeader className="p-10 bg-[#232F3E] text-white flex flex-row items-center justify-between sticky top-0 z-10">
                            <div>
                                <CardTitle className="text-3xl font-black uppercase tracking-tighter italic flex items-center gap-3">
                                    <Package className="w-8 h-8 text-[#FF9900]" /> Kòmand <span className="text-[#FF9900] ml-1">{selectedOrder.order_number}</span>
                                </CardTitle>
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-50 mt-1 flex items-center gap-2">
                                    <Calendar className="w-3 h-3" /> {new Date(selectedOrder.created_at).toLocaleString('fr-HT')}
                                </p>
                            </div>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => setSelectedOrder(null)}
                                className="w-12 h-12 rounded-2xl bg-white/5 hover:bg-white/10 text-white"
                            >
                                <X className="w-6 h-6" />
                            </Button>
                        </CardHeader>
                        
                        <CardContent className="p-10 space-y-10">
                            {/* Client & Address Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="p-8 bg-gray-50 rounded-[35px] border border-gray-100 flex flex-col gap-4">
                                    <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2">
                                        <User className="w-4 h-4 text-[#FF9900]" strokeWidth={3} /> Kliyan
                                    </p>
                                    <div className="space-y-1">
                                        <p className="text-xl font-black text-gray-900 tracking-tight">{selectedOrder.buyer_name}</p>
                                        <p className="text-sm font-bold text-gray-500">{selectedOrder.buyer_email}</p>
                                        <p className="text-sm font-bold text-[#FF9900]">{selectedOrder.buyer_phone}</p>
                                    </div>
                                </div>
                                
                                {selectedOrder.shipping_address && (
                                    <div className="p-8 bg-gray-50 rounded-[35px] border border-gray-100 flex flex-col gap-4">
                                        <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2">
                                            <Truck className="w-4 h-4 text-[#FF9900]" strokeWidth={3} /> Livrezon
                                        </p>
                                        <div className="space-y-1">
                                            <p className="font-black text-gray-900 tracking-tight">{selectedOrder.shipping_address.name}</p>
                                            <p className="text-xs font-bold text-gray-500 leading-relaxed">{selectedOrder.shipping_address.street}</p>
                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Items List */}
                            <div className="space-y-4">
                                <p className="text-[11px] font-black uppercase tracking-widest text-gray-400 ml-2 flex items-center gap-2">
                                    <ShoppingCart className="w-4 h-4 text-[#FF9900]" strokeWidth={3} /> Detay Pwodwi Amazon
                                </p>
                                <div className="space-y-3">
                                    {selectedOrder.items?.map((item, i) => (
                                        <div key={i} className="flex justify-between items-center p-6 bg-white border border-gray-50 rounded-[25px] shadow-sm hover:border-[#FF9900]/20 transition-colors group">
                                            <div className="flex items-center gap-6">
                                                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center font-black text-xs text-gray-300 group-hover:bg-[#FF9900]/5 group-hover:text-[#FF9900] transition-colors">
                                                    #{i+1}
                                                </div>
                                                <div>
                                                    <p className="font-black text-gray-900 leading-none group-hover:text-[#FF9900] transition-colors">{item.name}</p>
                                                    <div className="flex items-center gap-3 mt-2">
                                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-0.5 rounded">ASIN: {item.asin}</span>
                                                        <span className="text-[9px] font-black text-[#FF9900] uppercase tracking-wider bg-[#FF9900]/5 px-2 py-0.5 rounded">Kantite: {item.quantity}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-lg font-black text-gray-900 italic tracking-tighter">${(item.total || item.unit_price * item.quantity).toFixed(2)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pricing Summary */}
                            <div className="bg-[#232F3E] text-white p-10 rounded-[40px] shadow-2xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF9900] opacity-5 rounded-full blur-3xl -mr-20 -mt-20 group-hover:opacity-10 transition-opacity"></div>
                                
                                <div className="space-y-4 relative z-10">
                                    <div className="flex justify-between text-xs font-black uppercase tracking-[0.2em] opacity-40">
                                        <span>Amazon Subtotal:</span>
                                        <span>${selectedOrder.amazon_subtotal?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs font-black uppercase tracking-[0.2em] opacity-40 pb-4 border-b border-white/10">
                                        <span>Frè BVG (Sèvis):</span>
                                        <span>+${selectedOrder.bvgpromo_fee?.toFixed(2)}</span>
                                    </div>

                                    {selectedOrder.haiti_shipping_fee > 0 && (
                                        <div className="flex justify-between items-center p-6 bg-white/5 rounded-[25px] border border-white/5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-[#FF9900] rounded-xl flex items-center justify-center text-[#232F3E]">
                                                    <Truck className="w-5 h-5" strokeWidth={3} />
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-black uppercase tracking-widest">{selectedOrder.haiti_shipping_company}</p>
                                                    <p className="text-[9px] font-bold opacity-40">{selectedOrder.haiti_shipping_lbs} lbs • {selectedOrder.pay_shipping_now ? 'Peye Devan' : 'Peye an Ayiti'}</p>
                                                </div>
                                            </div>
                                            <p className="text-xl font-black tracking-tighter">${selectedOrder.haiti_shipping_fee.toFixed(2)}</p>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-end pt-6">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FF9900]">Gran Total USD</span>
                                            <span className="text-4xl font-black tracking-tighter italic text-white underline decoration-[#FF9900] decoration-8 underline-offset-[12px] decoration-solid/20">${selectedOrder.subtotal?.toFixed(2)}</span>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500">Total an Goud</span>
                                            <span className="text-4xl font-black text-[#FF9900] tracking-tighter italic">{selectedOrder.total_htg?.toLocaleString()} HTG</span>
                                        </div>
                                    </div>
                                    <div className="pt-6 text-[9px] text-center italic font-black uppercase tracking-[0.4em] opacity-20">
                                        Togo Exchange Rate: {selectedOrder.exchange_rate} HTG = 1 USD
                                    </div>
                                </div>
                            </div>
                            
                            <Button 
                                className="w-full h-20 rounded-[30px] bg-gray-50 hover:bg-black hover:text-white text-gray-400 font-black text-xl shadow-inner transition-all border border-gray-100 uppercase tracking-widest italic"
                                onClick={() => setSelectedOrder(null)}
                            >
                                Fèmen Detay Kòmand
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            )}
        </AdminLayout>
    );
};
